import { axiosInstance } from '@/lib/axios';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  MessageResponse,
  User,
} from '@/types/api.types';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

/**
 * Login with username and password
 */
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  // Backend expects JSON with email and password fields
  const response = await axiosInstance.post<LoginResponse>('/auth/login', {
    email: credentials.username,
    password: credentials.password,
  });

  // Store tokens in localStorage
  localStorage.setItem('access_token', response.data.access_token);
  if (response.data.refresh_token) {
    localStorage.setItem('refresh_token', response.data.refresh_token);
  }

  return response.data;
};

/**
 * Register a new user
 * Returns a message response (not a token) since email verification is required
 */
export const register = async (userData: RegisterRequest): Promise<MessageResponse> => {
  const response = await axiosInstance.post<MessageResponse>('/auth/register', userData);
  return response.data;
};

/**
 * Logout - clear local storage and invalidate session
 */
export const logout = async (): Promise<void> => {
  try {
    // Call backend logout endpoint (if exists)
    await axiosInstance.post('/auth/logout');
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Always clear local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }
};

/**
 * Get current user profile
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await axiosInstance.get<User>('/auth/me');
  return response.data;
};

/**
 * Refresh access token using refresh token
 */
export const refreshToken = async (refresh_token: string): Promise<LoginResponse> => {
  const response = await axiosInstance.post<LoginResponse>('/auth/refresh', {
    refresh_token,
  });

  // Update stored tokens
  localStorage.setItem('access_token', response.data.access_token);
  if (response.data.refresh_token) {
    localStorage.setItem('refresh_token', response.data.refresh_token);
  }

  return response.data;
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('access_token');
  return !!token;
};

/**
 * Get stored access token
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};

/**
 * Request password reset
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  await axiosInstance.post('/auth/password-reset/request', { email });
};

/**
 * Reset password with token
 */
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  await axiosInstance.post('/auth/password-reset/confirm', {
    token,
    new_password: newPassword,
  });
};

/**
 * Change password (authenticated user)
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  await axiosInstance.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
};

/**
 * Update user profile
 */
export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
  preferred_search_radius?: number;
  email_notifications?: boolean;
}

export const updateProfile = async (data: UpdateProfileRequest): Promise<User> => {
  const response = await axiosInstance.put<User>('/auth/profile', data);
  return response.data;
};

/**
 * Verify email address with token from email
 */
export const verifyEmail = async (token: string): Promise<MessageResponse> => {
  const response = await axiosInstance.post<MessageResponse>('/auth/verify-email', {
    token,
  });
  return response.data;
};
